import { useState, useRef, useEffect } from 'react';

interface AudioPlayerProps {
  src: string;
  onClose?: () => void;
}

export function AudioPlayer({ src, onClose }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration);
    const onEnd = () => setPlaying(false);

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlaying(!playing);
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * duration;
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="flex items-center gap-3 rounded-[12px]"
      style={{
        padding: '14px 18px',
        background: 'rgba(0,0,0,0.03)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <button
        onClick={toggle}
        className="shrink-0 flex items-center justify-center border-none cursor-pointer rounded-full"
        style={{
          width: 36,
          height: 36,
          background: '#D4582A',
          color: 'white',
        }}
      >
        {playing ? (
          <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        )}
      </button>

      <div className="flex-1 flex flex-col gap-1.5">
        <div
          className="w-full rounded-full cursor-pointer"
          style={{ height: 4, background: 'rgba(0,0,0,0.08)' }}
          onClick={seek}
        >
          <div
            className="rounded-full h-full"
            style={{
              width: `${progress}%`,
              background: '#D4582A',
              transition: 'width 0.1s linear',
            }}
          />
        </div>
        <div className="flex justify-between">
          <span className="font-mono" style={{ fontSize: 10.5, color: '#AAA' }}>
            {fmt(currentTime)}
          </span>
          <span className="font-mono" style={{ fontSize: 10.5, color: '#CCC' }}>
            {duration ? fmt(duration) : '--:--'}
          </span>
        </div>
      </div>

      {onClose && (
        <button
          onClick={onClose}
          className="border-none bg-transparent cursor-pointer p-0"
          style={{ color: '#AAA', fontSize: 18 }}
        >
          &times;
        </button>
      )}
    </div>
  );
}
